import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Global mobile navigation drawer
 * 
 * Features:
 * - Slide-in animation from left or right
 * - Backdrop with blur effect
 * - Focus trap (traps keyboard focus inside menu)
 * - Click outside to close
 * - Escape key to close (handled by useMobileMenu hook)
 * - Body scroll lock (handled by useMobileMenu hook)
 * 
 * Industry Pattern: GitHub Mobile, Linear Mobile, Stripe Dashboard
 * 
 * @example
 * ```tsx
 * const { isOpen, close } = useMobileMenu();
 * 
 * <MobileMenu isOpen={isOpen} onClose={close}>
 *   <nav>
 *     <a href="/dashboard">Dashboard</a>
 *     <a href="/reports">Reports</a>
 *   </nav>
 * </MobileMenu>
 * ```
 */

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  position?: 'left' | 'right';
  className?: string;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  children,
  position = 'left',
  className = ''
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Focus management: Store previous focus and restore on close
  useEffect(() => {
    if (isOpen) {
      // Store current active element
      previousActiveElement.current = document.activeElement as HTMLElement;
      
      // Focus first element when menu opens
      if (firstFocusableRef.current) {
        firstFocusableRef.current.focus();
      }
    } else {
      // Restore focus when menu closes
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }
  }, [isOpen]);

  // Focus trap: Prevent tabbing out of menu
  useEffect(() => {
    if (!isOpen) return;

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusableElements = menuRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (event.shiftKey) {
        // Shift + Tab: Moving backwards
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: Moving forwards
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);

    return () => {
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const slideDirection = position === 'left' 
    ? 'translate-x-0' 
    : 'translate-x-0';
    
  const slideOut = position === 'left'
    ? '-translate-x-full'
    : 'translate-x-full';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 z-[300]
          bg-black/50 backdrop-blur-sm
          transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0'}
        `}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu Drawer */}
      <div
        ref={menuRef}
        className={`
          fixed inset-y-0 ${position === 'left' ? 'left-0' : 'right-0'} z-[400]
          w-80 max-w-[85vw]
          bg-white dark:bg-slate-900
          shadow-2xl
          overflow-y-auto
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? slideDirection : slideOut}
          ${className}
        `}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        {/* Close button */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Menu
          </h2>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="
              p-2 rounded-lg
              text-gray-500 dark:text-gray-400
              hover:bg-gray-100 dark:hover:bg-slate-800
              hover:text-gray-900 dark:hover:text-white
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500
            "
            aria-label="Close mobile menu"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu content */}
        <div className="p-4">
          {children}
        </div>
      </div>
    </>
  );
};

